import React, { useEffect, useState } from "react";
import BookingsTable from "./BookingTable";
import PageHeader from "../../Components/PageHeader";
import axiosInstance from "../../utils/axiosInstance";

function BookingPage() {
  const [bookings, setBookings] = useState([]);
  const [hospital, setHospital] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("booked"); // Default: booked status
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  ); // Default: today's date
  const [showFilters, setShowFilters] = useState(false); // Toggle filter visibility

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axiosInstance.get("/bookings/report");
        const data = response.data;

        console.log("Fetched bookings:", data);

        if (Array.isArray(data.bookings)) {
          setBookings(data.bookings);
          setHospital(data.hospital || null);
        } else if (Array.isArray(data)) {
          setBookings(data);
        } else {
          console.error("Unexpected bookings data format:", data);
          setBookings([]);
        }
      } catch (error) {
        console.error("Failed to fetch bookings:", error);
        setError("Failed to load bookings. Please try again later.");
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  // Filter bookings based on search term, status, and date
  const filteredBookings = bookings.filter((booking) => {
    // Status filter
    const matchesStatus =
      statusFilter === "all" || booking.status === statusFilter;

    // Date filter (if date is selected, compare; if empty, show all dates)
    const matchesDate =
      selectedDate === "" ||
      new Date(booking.bookingDate).toISOString().split("T")[0] ===
        selectedDate;

    // Search filter (search in patient name, phone, doctor name, department)
    const matchesSearch =
      searchTerm === "" ||
      booking.patientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.patientPhoneNumber?.includes(searchTerm) ||
      booking.doctorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.departmentName?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesStatus && matchesDate && matchesSearch;
  });

  return (
    <div className="p-6">
      {/* Page Header */}
      <PageHeader
        title="Bookings"
        subtitle="View and manage patient appointments"
        showBackButton={true}
      />

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow-sm mb-4">
        {/* Toggle Button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition rounded-lg"
        >
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            <span className="text-sm font-medium text-gray-700">
              {showFilters ? "Hide Filters" : "Show Filters"}
            </span>
            {!showFilters &&
              (searchTerm ||
                statusFilter !== "booked" ||
                selectedDate !== new Date().toISOString().split("T")[0]) && (
                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                  Active
                </span>
              )}
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${
              showFilters ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Collapsible Filter Content */}
        {showFilters && (
          <div className="p-4 border-t border-gray-200 animate-fadeIn">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search Bar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  placeholder="Search by name, phone, doctor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="booked">Booked</option>
                  <option value="reserved">Reserved</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              {/* Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Booking Date (Optional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {selectedDate && (
                    <button
                      onClick={() => setSelectedDate("")}
                      className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                      title="Clear date filter"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mb-4 text-sm text-gray-600">
        Showing <strong>{filteredBookings.length}</strong> booking(s)
        {selectedDate && (
          <span>
            {" "}
            for <strong>{new Date(selectedDate).toLocaleDateString()}</strong>
          </span>
        )}
        {!selectedDate && (
          <span>
            {" "}
            for <strong>all dates</strong>
          </span>
        )}
        {searchTerm && (
          <span>
            {" "}
            matching "<strong>{searchTerm}</strong>"
          </span>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading bookings...</p>
        </div>
      ) : (
        /* Bookings Table */
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <BookingsTable bookings={filteredBookings} hospital={hospital} />
        </div>
      )}
    </div>
  );
}

export default BookingPage;
